import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SAHomeSkeleton } from './SASkeletons';
import { InviteModal } from './InviteModal';
import { Info, Warning } from '@phosphor-icons/react';
const monoFont = "'IBM Plex Mono', monospace";

interface Metrics {
  total_active_users: number;
  mrr_cents: number;
  new_users_month: number;
  inactive_users_count: number;
  prev_active_users: number;
  prev_mrr_cents: number;
}

interface Alert {
  text: string;
  color: string;
  link: string;
}

interface RecentUser {
  user_id: string;
  username: string;
  site_name: string;
  plan: string;
  status: string;
  last_access_at: string | null;
}

function formatCurrency(cents: number): string {
  return `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
}

function relativeTime(d: string | null) {
  if (!d) return 'nunca';
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'hoje';
  if (days === 1) return 'ontem';
  return `${days}d atrás`;
}

export function SAHome() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [convRate, setConvRate] = useState<number | null>(null);
  const [churn, setChurn] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      await supabase.rpc('refresh_admin_metrics');
      const { data: m } = await supabase.from('admin_metrics_cache').select('*').limit(1).maybeSingle();
      if (m) setMetrics(m as any);

      // Waitlist count
      const { count: wc } = await supabase.from('waitlist').select('id', { count: 'exact', head: true });
      setWaitlistCount(wc || 0);

      // Recent users
      const { data: recent } = await supabase.from('user_profiles')
        .select('user_id, username, site_name, plan, status, last_access_at')
        .order('created_at', { ascending: false }).limit(2);
      if (recent) setRecentUsers(recent as any[]);

      // Conversion rate
      const { count: totalInv } = await supabase.from('invites').select('id', { count: 'exact', head: true }).neq('status', 'cancelled');
      const { count: accInv } = await supabase.from('invites').select('id', { count: 'exact', head: true }).eq('status', 'accepted');
      if (totalInv && totalInv > 0) setConvRate(Math.round(((accInv || 0) / totalInv) * 100));

      // Churn: cancelled last 30d / active previous month
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
      const { count: cancelled30d } = await supabase.from('user_profiles')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'cancelled')
        .gte('updated_at', thirtyDaysAgo);
      const prevActive = (m as any)?.prev_active_users || 1;
      setChurn(Math.round(((cancelled30d || 0) / prevActive) * 100));

      // Alerts
      const alertsList: Alert[] = [];
      if (m && (m as any).inactive_users_count > 0) {
        alertsList.push({
          text: `${(m as any).inactive_users_count} usuários inativos há mais de 30 dias`,
          color: '#F0E614',
          link: '/admin/usuarios?filtro=inativos',
        });
      }
      // Users who never published
      const { data: profiles } = await supabase.from('user_profiles').select('user_id').eq('status', 'active');
      let emptyCount = 0;
      if (profiles) {
        for (const p of profiles) {
          const { count } = await supabase.from('projects').select('id', { count: 'exact', head: true }).eq('user_id', p.user_id).eq('published', true);
          if ((count ?? 0) === 0) emptyCount++;
        }
      }
      if (emptyCount > 0) {
        alertsList.push({
          text: `${emptyCount} usuário${emptyCount > 1 ? 's' : ''} nunca publicou`,
          color: '#F0E614',
          link: '/admin/usuarios?filtro=nunca_publicou',
        });
      }
      if ((wc || 0) > 0) {
        alertsList.push({
          text: `${wc} inscritos na lista de espera`,
          color: '#0057FF',
          link: '/admin/espera',
        });
      }
      setAlerts(alertsList);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <SAHomeSkeleton />;

  const m = metrics || { total_active_users: 0, mrr_cents: 0, new_users_month: 0, inactive_users_count: 0, prev_active_users: 0, prev_mrr_cents: 0 };

  const kpis = [
    { label: 'MRR', value: formatCurrency(m.mrr_cents), diff: m.mrr_cents - m.prev_mrr_cents, format: (d: number) => `${d >= 0 ? '↑' : '↓'} ${formatCurrency(Math.abs(d))}` },
    { label: 'ATIVOS', value: String(m.total_active_users), diff: m.total_active_users - m.prev_active_users, format: (d: number) => d === 0 ? '—' : `${d > 0 ? '↑' : '↓'} ${Math.abs(d)}` },
    { label: 'CHURN MENSAL', value: churn !== null ? `${churn}%` : '—', diff: 0, format: () => '—' },
    { label: 'CONV. CONVITE', value: convRate !== null ? `${convRate}%` : '—', diff: 0, format: () => '—' },
  ];

  const statusColors: Record<string, string> = { active: '#00E87A', suspended: '#F0E614', cancelled: '#FF3F2D', pending: 'rgba(247,246,242,0.4)' };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: monoFont, fontSize: 18, fontWeight: 300, color: '#F7F6F2' }}>Dashboard</div>
          <div className="hidden md:block" style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>
            CRIAAA — ADMIN
          </div>
        </div>
        <div style={{
          width: 32, height: 32, backgroundColor: 'rgba(247,246,242,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: monoFont, fontSize: 14, fontWeight: 300, color: 'rgba(247,246,242,0.5)',
        }}>T</div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', padding: 16, marginBottom: 20 }}>
          <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 12 }}>
            REQUER ATENÇÃO
          </div>
          {alerts.map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: a.color, flexShrink: 0 }} />
                <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.7)' }}>{a.text}</span>
              </div>
              <button
                onClick={() => navigate(a.link)}
                style={{
                  fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                  color: 'rgba(247,246,242,0.4)', background: 'none', border: 'none', cursor: 'pointer',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#F7F6F2'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(247,246,242,0.4)'}
              >VER →</button>
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', padding: 16 }}>
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 8 }}>
              {k.label}
            </div>
            <div style={{ fontFamily: monoFont, fontSize: 20, fontWeight: 300, color: '#F7F6F2', marginBottom: 4 }}>
              {k.value}
            </div>
            <div style={{
              fontFamily: monoFont, fontSize: 12, fontWeight: 300,
              color: k.diff > 0 ? '#00E87A' : k.diff < 0 ? '#FF3F2D' : 'rgba(247,246,242,0.3)',
            }}>
              {k.format(k.diff)}
            </div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => setShowInviteModal(true)}
          style={{
            flex: 1, fontFamily: monoFont, fontSize: 12, fontWeight: 300,
            padding: '12px', border: '0.5px solid #00E87A',
            backgroundColor: 'rgba(0,232,122,0.08)', color: '#00E87A',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >+ Gerar convite</button>
        <button
          onClick={() => navigate('/admin/espera')}
          style={{
            flex: 1, fontFamily: monoFont, fontSize: 12, fontWeight: 300,
            padding: '12px', border: '0.5px solid rgba(247,246,242,0.12)',
            backgroundColor: 'transparent', color: 'rgba(247,246,242,0.6)',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >Ver espera ({waitlistCount})</button>
      </div>

      {/* Recent users */}
      {recentUsers.length > 0 && (
        <>
          <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 12 }}>
            USUÁRIOS RECENTES
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recentUsers.map(u => (
              <button
                key={u.user_id}
                onClick={() => navigate(`/admin/usuarios/${u.user_id}`)}
                style={{
                  backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)',
                  padding: 14, display: 'flex', alignItems: 'center', gap: 12,
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(247,246,242,0.15)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(247,246,242,0.12)'}
              >
                <div style={{
                  width: 36, height: 36, backgroundColor: 'rgba(247,246,242,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: monoFont, fontSize: 14, fontWeight: 300, color: 'rgba(247,246,242,0.5)',
                  flexShrink: 0,
                }}>
                  {u.username[0]?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: '#F7F6F2' }}>{u.username}</div>
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)' }}>
                    Acesso: {relativeTime(u.last_access_at)} · {u.plan.toUpperCase()}
                  </div>
                </div>
                <span style={{
                  fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                  padding: '2px 6px', border: `0.5px solid ${statusColors[u.status] || 'rgba(247,246,242,0.2)'}`,
                  color: statusColors[u.status] || 'rgba(247,246,242,0.4)',
                }}>
                  {u.status === 'active' ? 'ATIVO' : u.status === 'suspended' ? 'SUSPENSO' : u.status.toUpperCase()}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {showInviteModal && (
        <InviteModal onClose={() => setShowInviteModal(false)} onInviteSent={() => setShowInviteModal(false)} />
      )}
    </div>
  );
}
